// Presentation-only formatting helpers. They never recalculate money: values
// are formatted exactly as received (string or number) with the currency
// code shown as a label. Precision is preserved by formatting the decimal
// string the API sent, not by re-rounding.

const localeFor = (language) => (String(language || "en").startsWith("ar") ? "ar-LY" : "en-US");

// Keep Western digits for identifiers and money so mixed-direction content
// stays unambiguous; Arabic UI text still reads RTL around them. Amounts and
// counts use "." as the decimal and "," as the grouping separator in BOTH
// languages: ar-LY swaps them ("10,000" for 10.000 LYD, "1.234" for 1234),
// which reads as a different number (browser acceptance N1). Every number
// formatter of the admin uses this locale.
export const NUMBER_LOCALE = "en-US";

export const formatNumber = (value, { minimumFractionDigits, maximumFractionDigits = 2 } = {}) => {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return new Intl.NumberFormat(NUMBER_LOCALE, { minimumFractionDigits, maximumFractionDigits }).format(numeric);
};

export const formatInteger = (value) => formatNumber(value, { maximumFractionDigits: 0 });

// Money: two fixed decimals, grouped; currency code rendered separately by
// the caller (or appended with `withCode`). Non-numeric input is returned
// verbatim so an unexpected API value is never silently reshaped.
export const formatMoney = (value, currencyCode, { withCode = true, fractionDigits = 2 } = {}) => {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  const formatted = new Intl.NumberFormat(NUMBER_LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(numeric);
  const code = String(currencyCode || "").toUpperCase();
  return withCode && code ? `${formatted} ${code}` : formatted;
};

export const formatPercent = (value, fractionDigits = 2) => {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return `${new Intl.NumberFormat(NUMBER_LOCALE, { maximumFractionDigits: fractionDigits }).format(numeric)}%`;
};

export const formatDateTime = (value, language, options) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(localeFor(language), {
    dateStyle: "medium",
    timeStyle: "short",
    numberingSystem: "latn",
    ...options,
  }).format(date);
};

export const formatDate = (value, language) => formatDateTime(value, language, { dateStyle: "medium", timeStyle: undefined });

export const formatRelativeTime = (value, language) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const abs = Math.abs(diffSeconds);
  const formatter = new Intl.RelativeTimeFormat(localeFor(language), { numeric: "auto" });
  if (abs < 60) return formatter.format(diffSeconds, "second");
  if (abs < 3600) return formatter.format(Math.round(diffSeconds / 60), "minute");
  if (abs < 86400) return formatter.format(Math.round(diffSeconds / 3600), "hour");
  return formatter.format(Math.round(diffSeconds / 86400), "day");
};

export const truncateMiddle = (value, max = 18) => {
  const text = String(value ?? "");
  if (text.length <= max) return text;
  const keep = Math.max(4, Math.floor((max - 1) / 2));
  return `${text.slice(0, keep)}…${text.slice(-keep)}`;
};

export const initialsOf = (name = "") =>
  String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
